import React, { useState, useEffect } from 'react'
import { Input } from "@/components/ui/input"
import { visibilityType } from '../views/CatalogueTableView';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ACCESS_OPTIONS } from '../others/Filters';
import { apiSwaggerClientSide } from '@/shared/api';
import { Group, KeycloakUser } from '@/sharedTypes/Api';
import { AdminCatalogueView, SelectedSample } from '../Catalogue';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import toast from '@/components/ui/custom-toast';

interface IAccessControlPanelView {
  selectedSamples: SelectedSample[];
  setSelectedSamples: React.Dispatch<React.SetStateAction<SelectedSample[]>>;
  setView: React.Dispatch<React.SetStateAction<AdminCatalogueView>>;
}

const AccessControlPanelView = ({ selectedSamples, setSelectedSamples, setView }: IAccessControlPanelView) => {
  const initialVisibility = selectedSamples[0].sample.isPrivate ?
  'hidden' :
  (selectedSamples[0].sample.accessControl.allowedGroups.length > 0 || selectedSamples[0].sample.accessControl.allowedUsers.length > 0) ? 'restricted' : 'public';

  const [newVisibility, setNewVisibility] = useState<visibilityType>(initialVisibility);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [allowedGroups, setAllowedGroups] = useState<{ _id: string; name: string }[]>([]);
  const [allowedUsers, setAllowedUsers] = useState<{ sub: string; username: string }[]>([]);
  const [searchedUsers, setSearchedUsers] = useState<KeycloakUser[]>([]);
  const [searchedGroups,setSearchedGroups] = useState<Group[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  // Track existing items marked for removal per sample: Map<sampleId, Set<groupId/userId>>
  const [removedGroups, setRemovedGroups] = useState<Map<string, Set<string>>>(new Map());
  const [removedUsers, setRemovedUsers] = useState<Map<string, Set<string>>>(new Map());

  // Debounced user search
  useEffect(() => {
    if (userSearchQuery.length < 2) {
      setSearchedUsers([]);
      return;
    }

    const debounce = setTimeout(async () => {
      try {
        setIsDataLoading(true);
        const res = await apiSwaggerClientSide.admin.usersSearchList({ q: userSearchQuery });
        if (res.data.data?.length) {
          setSearchedUsers(res.data.data);
        } else {
          setSearchedUsers([]);
        }
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setIsDataLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [userSearchQuery]);

  // ** Mock removing all the users and groups(as well as new ones) from selected samples when visibility is changed to 'public'
  // * If hidden -> no changes needed
  useEffect(() => {
    if(newVisibility !== 'public') return;

    const groupsToRemove = new Map<string, Set<string>>();
    const usersToRemove = new Map<string, Set<string>>();

    selectedSamples.forEach(sample => {
      const groupSet = new Set<string>();
      sample.sample.accessControl.allowedGroups.forEach(group => {
        groupSet.add(group._id);
      });
      if (groupSet.size > 0) {
        groupsToRemove.set(sample.id, groupSet);
      }

      const userSet = new Set<string>();
      sample.sample.accessControl.allowedUsers.forEach(user => {
        userSet.add(user.sub);
      });
      if (userSet.size > 0) {
        usersToRemove.set(sample.id, userSet);
      }
    });

    // Remove new users and groups as well
    setAllowedGroups([]);
    setAllowedUsers([]);

    setRemovedGroups(groupsToRemove);
    setRemovedUsers(usersToRemove);

    // If afterwards set back to restricted/hidden, clear the removals
    return () => {
      setRemovedGroups(new Map());
      setRemovedUsers(new Map());
    }
  }, [newVisibility])

  const handleSave = async () => {
    try {
      setIsDataLoading(true);
      const isPrivate = newVisibility === 'hidden';
      const groups = allowedGroups.map(g => g._id);
      const users = allowedUsers.map(u => u.sub);

      // Process each sample individually to handle per-sample removals
      const promises = selectedSamples.map(sample => {
        const groupsToRemove = Array.from(removedGroups.get(sample.id) || []);
        const usersToRemove = Array.from(removedUsers.get(sample.id) || []);

        return apiSwaggerClientSide.admin.samplesBulkCreate({
          sampleIds: [sample.id],
          groups,
          isPrivate,
          users,
          groupsToRemove,
          usersToRemove
        });
      });

      await Promise.all(promises);

      toast({
        title: "Access control changes saved",
        description: "The access control settings have been successfully updated.",
        type: 'success'
      });
      // Clear selections after successful save so fresh data is fetched on re-select
      setSelectedSamples([]);
    } catch(err) {
      console.error("Error saving access control changes:", err);
      toast({
        title: "Error saving access control changes",
        description: "There was an error while updating the access control settings. Please try again.",
        type: 'error'
      });
    } finally {
      setIsDataLoading(false);
      goBack();
    }
  }

  // Toggle removal of existing group for a specific sample
  const toggleRemoveGroup = (sampleId: string, groupId: string) => {
    setRemovedGroups(prev => {
      const next = new Map(prev);
      const sampleSet = new Set(next.get(sampleId) || []);
      if (sampleSet.has(groupId)) {
        sampleSet.delete(groupId);
      } else {
        sampleSet.add(groupId);
      }
      if (sampleSet.size === 0) {
        next.delete(sampleId);
      } else {
        next.set(sampleId, sampleSet);
      }
      return next;
    });
  };

  // Toggle removal of existing user for a specific sample
  const toggleRemoveUser = (sampleId: string, userId: string) => {
    setRemovedUsers(prev => {
      const next = new Map(prev);
      const sampleSet = new Set(next.get(sampleId) || []);
      if (sampleSet.has(userId)) {
        sampleSet.delete(userId);
      } else {
        sampleSet.add(userId);
      }
      if (sampleSet.size === 0) {
        next.delete(sampleId);
      } else {
        next.set(sampleId, sampleSet);
      }
      return next;
    });
  };

  // Check if a group is marked for removal for a specific sample
  const isGroupMarkedForRemoval = (sampleId: string, groupId: string) => {
    return removedGroups.get(sampleId)?.has(groupId) || false;
  };

  // Check if a user is marked for removal for a specific sample
  const isUserMarkedForRemoval = (sampleId: string, userId: string) => {
    return removedUsers.get(sampleId)?.has(userId) || false;
  };

  // Remove newly added group
  const removeNewGroup = (groupId: string) => {
    setAllowedGroups(prev => prev.filter(g => g._id !== groupId));
  };

  // Remove newly added user
  const removeNewUser = (userSub: string) => {
    setAllowedUsers(prev => prev.filter(u => u.sub !== userSub));
  };

  const handleCancel = () => {
    // Reset states
    setNewVisibility(initialVisibility);
    setAllowedGroups([]);
    setAllowedUsers([]);
    setSearchedGroups([]);
    setSearchedUsers([]);
    setGroupSearchQuery('');
    setUserSearchQuery('');
    setRemovedGroups(new Map());
    setRemovedUsers(new Map());
    setIsDataLoading(false);
  }

  const goBack = () => {
    handleCancel();
    setView('CATALOGUE');
  }

  return (
    <div className='border-1 border-gray-800 flex gap-0 flex-col h-full'>
      <div className="flex gap-0 h-full">
        {/* Left Configuration Panel */}
        <div className='py-2 px-3 flex flex-col items-start h-full basis-[35%] border-r-1 border-gray-800'>
          <h2 className='text-base font-bold uppercase tracking-widest text-muted-foreground mb-4'>Configuration</h2>

          {/* Visibility */}
          <div className="flex flex-col gap-2 w-full mb-4">
            <label className="text-sm font-medium">Visibility</label>
            <Select
            value={newVisibility}
            onValueChange={(value) => setNewVisibility(value as visibilityType)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="Access type" />
            </SelectTrigger>
            <SelectContent>
              {ACCESS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          </div>
          {newVisibility === 'restricted' && (
            <>
              <div className="flex flex-col gap-2 h-full w-full max-h-[200px] overflow-y-auto px-2 border-1 py-2 rounded-sm mt-auto">
                <label className="text-sm font-medium">Add Groups 
                  <span className='text-xs text-muted-foreground'>
                    (Please enter at least 2 characters)
                  </span>
                </label>
                <Input 
                type="text" 
                placeholder='Search...'
                value={groupSearchQuery}
                onChange={(e) => setGroupSearchQuery(e.target.value)}
                />
                <div>
                  {searchedGroups.filter(group => !allowedGroups.some(allowedGroup => allowedGroup._id === group._id)).map((group: Group) => (
                    <button 
                      key={group._id} 
                      className='p-2 hover:bg-gray-200 cursor-pointer bg-transparent w-full border-0 text-left text-sm'
                      onClick={() => {
                        const _id = group._id;
                        const name = group.name;
                        if (_id && name) {
                          setAllowedGroups((prev) => [...prev, { _id, name }]);
                        }
                        setGroupSearchQuery('');
                      }}
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 h-full w-full max-h-[200px] overflow-y-auto px-2 border-1 py-2 rounded-sm mt-auto">
                <label className="text-sm font-medium">Add Users 
                  <span className='text-xs text-muted-foreground'>
                    (Please enter at least 2 characters)
                  </span>
                </label>
                <Input 
                type="text" 
                placeholder='Search...'
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                />
              {
                searchedUsers.length > 0 ? (
                  <div className='max-h-[150px] overflow-y-auto w-full'>
                    {searchedUsers.filter(user => !allowedUsers.some(allowedUser => allowedUser.sub === user.sub)).map((user: KeycloakUser) => (
                      <button
                        key={user.sub} 
                        className='p-2 hover:bg-gray-200 cursor-pointer bg-transparent w-full border-0 text-left text-sm'
                        onClick={() => {
                          const sub = user.sub;
                          const username = user.username;
                          if (sub && username) {
                            setAllowedUsers((prev) => [...prev, { sub, username }]);
                          }
                          setUserSearchQuery('');
                        }}

                      >
                        {user.username} ({user.email})
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className='text-sm text-muted-foreground mt-2'>
                    {isDataLoading ? 'Searching users...' : 'No users found'}
                  </div>
                )
              }
              </div>
            </>
          )}

        </div>
        {/* Right Review Changes Panel */}
        <div className='py-2 px-3 flex flex-col justify-start items-start h-full basis-[65%] overflow-hidden'>
          <h2 className='text-base font-bold uppercase tracking-widest text-muted-foreground mb-4'>Review Changes</h2>

          <div className='flex-1 w-full overflow-y-auto'>
            {selectedSamples.map((item) => (
              <div key={item.id} className='mb-4 p-3 border rounded-md bg-gray-50'>
                <h4 className='text-sm font-semibold mb-2'>{item.sample.title}</h4>

                {/* Groups */}
                <div className='mb-2'>
                  <span className='text-xs text-muted-foreground'>Groups: </span>
                  {item.sample.accessControl.allowedGroups.length > 0 ? (
                    <span className='inline-flex flex-wrap gap-1'>
                      {item.sample.accessControl.allowedGroups.map((group) => {
                        const isMarkedForRemoval = isGroupMarkedForRemoval(item.id, group._id);
                        return (
                          <button
                            key={group._id}
                            onClick={() => toggleRemoveGroup(item.id, group._id)}
                            className={`px-2 py-0.5 text-xs rounded cursor-pointer border-0 ${
                              isMarkedForRemoval
                                ? 'bg-red-200 text-red-800 line-through hover:bg-red-300'
                                : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                            }`}
                          >
                            {group.name}
                          </button>
                        );
                      })}
                    </span>
                  ) : (
                    <span className='text-xs text-muted-foreground italic'>None</span>
                  )}
                </div>

                {/* Users */}
                <div>
                  <span className='text-xs text-muted-foreground'>Users: </span>
                  {item.sample.accessControl.allowedUsers.length > 0 ? (
                    <span className='inline-flex flex-wrap gap-1'>
                      {item.sample.accessControl.allowedUsers.map((user) => {
                        const isMarkedForRemoval = isUserMarkedForRemoval(item.id, user.sub);
                        return (
                          <button
                            key={user.sub}
                            onClick={() => toggleRemoveUser(item.id, user.sub)}
                            className={`px-2 py-0.5 text-xs rounded cursor-pointer border-0 ${
                              isMarkedForRemoval
                                ? 'bg-red-200 text-red-800 line-through hover:bg-red-300'
                                : 'bg-green-100 text-green-800 hover:bg-green-200'
                            }`}
                          >
                            {user.username}
                          </button>
                        );
                      })}
                    </span>
                  ) : (
                    <span className='text-xs text-muted-foreground italic'>None</span>
                  )}
                </div>

                {/* New Groups */}
                {allowedGroups.length > 0 && (
                  <div className='mt-2'>
                    <span className='text-xs text-muted-foreground'>New Allowed Groups: </span>
                    <span className='inline-flex flex-wrap gap-1'>
                      {allowedGroups.map((group) => (
                        <button
                          key={group._id}
                          onClick={() => removeNewGroup(group._id)}
                          className='px-2 py-0.5 text-xs bg-blue-200 text-blue-900 rounded cursor-pointer border-0 hover:bg-red-200 hover:text-red-800'
                        >
                          {group.name}
                        </button>
                      ))}
                    </span>
                  </div>
                )}

                {/* New Users */}
                {allowedUsers.length > 0 && (
                  <div className='mt-2'>
                    <span className='text-xs text-muted-foreground'>New Allowed Users: </span>
                    <span className='inline-flex flex-wrap gap-1'>
                      {allowedUsers.map((user) => (
                        <button
                          key={user.sub}
                          onClick={() => removeNewUser(user.sub)}
                          className='px-2 py-0.5 text-xs bg-green-200 text-green-900 rounded cursor-pointer border-0 hover:bg-red-200 hover:text-red-800'
                        >
                          {user.username}
                        </button>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='flex p-4 justify-between border-1 border-t-gray-800'>
        <ButtonShadcn 
          variant="default"
          onClick={goBack}
        >
          Back to Catalogue
        </ButtonShadcn>
        <div className='flex gap-4'>
          <ButtonShadcn
            variant="secondary"
            onClick={handleCancel}
            >
            Cancel
          </ButtonShadcn>
          {/* Here need to make it disabled based on real state changes track */}
          <ButtonShadcn
            variant="default"
            onClick={handleSave}
            disabled={isDataLoading}
          >
            Save Changes
          </ButtonShadcn>
        </div>
      </div>
    </div>
  )
}

export default AccessControlPanelView